import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { codeToHtml } from "shiki";
import "./CodeEditor.css";

const EDITOR_SHIKI_THEME = "github-dark";
const EDITOR_LINE_HEIGHT_PX = 19;
const EDITOR_PAD_TB = 12;
const EDITOR_MAX_BODY_PX = 720;

export const CodeEditor = ({ initialCode = "", highlightLang }) => {
  const [code, setCode] = useState(initialCode);
  const [hlHtml, setHlHtml] = useState(null);
  const shikiScrollRef = useRef(null);

  const naturalBodyHeight = useMemo(() => {
    const lines = Math.max(1, code.split("\n").length);
    return Math.max(350, lines * EDITOR_LINE_HEIGHT_PX + EDITOR_PAD_TB * 2);
  }, [code]);

  const bodyHeight = Math.min(EDITOR_MAX_BODY_PX, naturalBodyHeight);
  const scrollBody = naturalBodyHeight > EDITOR_MAX_BODY_PX;

  useEffect(() => {
    if (!highlightLang) {
      setHlHtml(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const html = await codeToHtml(code, {
          lang: highlightLang,
          theme: EDITOR_SHIKI_THEME,
        });
        if (!cancelled) setHlHtml(html);
      } catch {
        try {
          const html = await codeToHtml(code, {
            lang: "text",
            theme: EDITOR_SHIKI_THEME,
          });
          if (!cancelled) setHlHtml(html);
        } catch {
          if (!cancelled) setHlHtml(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, highlightLang]);

  const showShiki = Boolean(highlightLang && hlHtml);

  const syncShikiScroll = useCallback((el) => {
    const sh = shikiScrollRef.current;
    if (!sh || !el) return;
    sh.scrollTop = el.scrollTop;
    sh.scrollLeft = el.scrollLeft;
  }, []);

  return (
    <div className="code-editor">
      <div className="code-editor-header">
        <div className="code-editor-header-left">
          <span className="code-editor-filename">Contract.sol</span>
          <div className="code-editor-dot" />
        </div>
        <span className="code-editor-lines">{code.split("\n").length} lines</span>
      </div>

      <div
        className="code-editor-content"
        style={showShiki ? { minHeight: bodyHeight } : undefined}
      >
        <div
          className="code-editor-line-numbers"
          style={showShiki ? { minHeight: bodyHeight } : undefined}
        >
          {code.split("\n").map((_, idx) => (
            <div key={idx} className="code-editor-line-number">
              {idx + 1}
            </div>
          ))}
        </div>

        <div
          className={`code-editor-edit-wrap${showShiki ? " code-editor-edit-wrap--shiki" : ""}`}
          style={
            showShiki
              ? { minHeight: bodyHeight, height: bodyHeight }
              : undefined
          }
        >
          {showShiki && (
            <div
              ref={shikiScrollRef}
              className="code-editor-shiki-scroll"
              aria-hidden
            >
              <div
                className="code-editor-shiki-layer"
                style={{ minHeight: naturalBodyHeight }}
                dangerouslySetInnerHTML={{ __html: hlHtml }}
              />
            </div>
          )}
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onScroll={(e) => showShiki && syncShikiScroll(e.currentTarget)}
            className={`code-editor-textarea${showShiki ? " code-editor-textarea--shiki" : ""}`}
            style={
              showShiki
                ? {
                    tabSize: 2,
                    caretColor: "#cccccc",
                    resize: "none",
                    overflowX: "auto",
                    overflowY: scrollBody ? "auto" : "hidden",
                  }
                : {
                    tabSize: 2,
                    caretColor: "#cccccc",
                  }
            }
            spellCheck={false}
            placeholder="// Write your Solidity code here..."
          />
        </div>
      </div>
    </div>
  );
};
